import { Box, Button, CircularProgress, CircularProgressProps, Drawer, Fade, Grid, IconButton, Stack, TextField, Typography, alpha, lighten, useTheme } from "@mui/material";
import React, { useEffect, useState } from "react";
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import Dictaphone, { isDictphoneAvailable } from "./SpeechInput";
import KeyboardVoiceRoundedIcon from '@mui/icons-material/KeyboardVoiceRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import { Pulse } from "../../../../components/animations";
import { IUserInput, TMessage, TUserInputRequirement } from "../../../../../generated/gql/graphql";
import WebContentUploadBanner from "../../../../components/input/WebContent";
import DatasetSelect from "../../../../components/input/datasetSelect";
import FileSelect from "../../../../components/input/fileSelect";
import StaticParamEditor from "../../../../components/pixie/param-editor/DEPRECATED/StaticParamEditor";
import { useClientStore } from "../../../../hooks/ClientState";
import { useUserAndWorkspaceStore } from "../../../../hooks/UserAndWorkspaceStore";
import PauseRoundedIcon from '@mui/icons-material/PauseRounded';
import WebInteractionOutcomeUploadBanner from "../../../../components/input/WebInteractionOutcome";
import { captureWebContent } from "../../../../utils/webContent";
import { runJsCode, runPyCode } from "../../../../utils/runCode";
import Ajv from "ajv";
import CodeExecutionBanner from "../../../../components/input/CodeExecution";

function ChoicesInput(props: {
  choices: string[];
  isMultiple: boolean;
  choicesOnly?: boolean;
  value: IUserInput;
  onChange: (chatInput: IUserInput) => void;
  onSubmit: (chatInput: IUserInput) => void;
}): React.ReactElement {
  const [selectedChoices, setSelectedChoices] = useState<string[]>([]);
  const theme = useTheme();

  useEffect(() => {
    setSelectedChoices([]);
  }, [props.choices]);

  // Check if the option text is long enough to require a full width column
  const needsFullWidth = (text: string) => {
    // If text is longer than 25 characters, use full width
    return text.length > 25;
  };

  // Pre-calculate layout to avoid lone single-column items
  const getItemLayout = (choices: string[]) => {
    const layout: boolean[] = []; // true = full width, false = half width
    let currentRow: number[] = []; // Indices of items in current row

    // First pass: determine natural width based on text length
    choices.forEach((text, i) => {
      layout[i] = needsFullWidth(text);

      // Track items in current row
      if (layout[i]) {
        // This is a full-width item, so it's on its own row
        if (currentRow.length === 1) {
          // If there's a single item in the current row, make it full width
          layout[currentRow[0]] = true;
        }
        currentRow = [];
      } else {
        // This is a half-width item
        currentRow.push(i);
        if (currentRow.length === 2) {
          // We have two half-width items, row is full
          currentRow = [];
        }
      }
    });

    // Handle the last row if it has a single half-width item
    if (currentRow.length === 1) {
      layout[currentRow[0]] = true;
    }

    return layout;
  };

  const buttonStyle = (selected: boolean) => ({
    p: 2,
    borderRadius: 2,
    minHeight: '56px',
    textAlign: 'left', // Left aligned text
    justifyContent: 'flex-start',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    textTransform: 'none',
    ...(selected ? {
      boxShadow: theme.shadows[8],
      background: `linear-gradient(to top right, ${lighten(theme.palette.primary.dark, 0.2)}, ${theme.palette.primary.dark})`,
      color: theme.palette.getContrastText(theme.palette.primary.dark),
      border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
      transform: 'translateY(-2px)',
      '&:hover': {
        background: `linear-gradient(to top right, ${lighten(theme.palette.primary.dark, 0.3)}, ${theme.palette.primary.dark})`,
      }
    } : {
      borderColor: theme.palette.primary.dark,
      color: theme.palette.primary.dark,
      '&:hover': {
        background: alpha(theme.palette.primary.main, 0.08),
      }
    })
  });

  if (props.isMultiple) {
    const itemLayouts = getItemLayout(props.choices);

    return <Grid container spacing={2} justifyContent="center" width='100%'>
      {props.choices.map((opt, j) => {
        const isSelected = selectedChoices.includes(opt);
        const isFullWidth = itemLayouts[j];

        return <Grid
          key={`opt-${j}`}
          size={{
            xs: 12,
            sm: isFullWidth ? 12 : 6
          }}
        >
          <Button
            fullWidth
            variant={isSelected ? 'contained' : 'outlined'}
            onClick={() => {
              const selected = isSelected
                ? selectedChoices.filter(item => item !== opt)
                : [...selectedChoices, opt];
              props.onChange({ ...props.value, message: selected.join(', '), choices: selected });
              setSelectedChoices(selected);
            }}
            sx={buttonStyle(isSelected)}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5
            }}>
              <CheckRoundedIcon sx={{
                opacity: isSelected ? 1 : 0.4,
                color: isSelected
                  ? theme.palette.getContrastText(theme.palette.primary.dark)
                  : alpha(theme.palette.primary.dark, 0.7)
              }} />
              <Typography>{opt}</Typography>
            </Box>
          </Button>
        </Grid>
      })}
      {props.choicesOnly &&
        <Grid size={12}>
          <Button
            fullWidth
            variant='contained'
            color="success"
            disabled={selectedChoices.length === 0}
            onClick={() => props.onSubmit({
              ...props.value,
              message: selectedChoices.join(', '),
              choices: selectedChoices
            })}
            sx={{ p: 1.5, borderRadius: 2 }}
          >
            <CheckRoundedIcon />
          </Button>
        </Grid>
      }
    </Grid>;
  } else {
    const itemLayouts = getItemLayout(props.choices);

    return <Grid container spacing={2} justifyContent="center" width='100%'>
      {props.choices.map((opt, j) => {
        const isFullWidth = itemLayouts[j];

        return (
          <Grid
            key={`opt-${j}`}
            size={{
              xs: 12,
              sm: isFullWidth ? 12 : 6
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                props.onChange({ ...props.value, message: opt, choices: [opt] });
                props.onSubmit({ ...props.value, message: opt, choices: [opt] });
              }}
              sx={buttonStyle(false)}
            >
              <Typography>{opt}</Typography>
            </Button>
          </Grid>
        );
      })}
    </Grid>
  }
}

export function AdditionalInputView(props: {
  inputRequirement: TUserInputRequirement,
  value: IUserInput,
  onChange: (chatInput: IUserInput) => void,
  onSubmit: (chatInput: IUserInput) => void,
  show: boolean,
  conversation?: TMessage[],
}): React.ReactElement {
  const theme = useTheme();
  const workspaceId = useUserAndWorkspaceStore(state => state.workspaceId);
  const appId = useClientStore(state => state.flowId);

  const filesToMessage = (files: string[]) => files.length == 0 ? "Nothing to upload" : 'Uploaded: ' + files.join(', ')


  return <Fade
    in={props.show}
    timeout={{
      enter: 300,
      exit: 200
    }}
    style={{
      pointerEvents: props.show ? 'auto' : 'none',
    }}
  >
    <Box
      display='flex'
      maxHeight='100%'
      width='100%'
      overflow='auto'
    >
      {Boolean(props.inputRequirement.choices?.length) && <ChoicesInput
        choices={props.inputRequirement.choices}
        isMultiple={Boolean(props.inputRequirement.choicesMultiple)}
        choicesOnly={props.inputRequirement.choicesOnly}
        value={props.value}
        onChange={props.onChange}
        onSubmit={props.onSubmit}
      />
      }
      {props.inputRequirement.webContent && <WebContentUploadBanner />}
      {props.inputRequirement.webInteractionOutcome && <WebInteractionOutcomeUploadBanner />}
      {props.inputRequirement.codeExecution && <CodeExecutionBanner />}
      {props.inputRequirement.knowledgeSelect && <Box width='100%' display='flex' justifyContent='center'>
        {/* TODO knowledge-rfc: KnowledgeBaseSelect */}
        <Typography>Not Implemented</Typography>
      </Box>
      }
      {props.inputRequirement.fileSelect && <FileSelect
        label="Select a file"
        fileId={props.value.fileId}
        onChange={fileId => props.onChange({ ...props.value, fileId })}
        onSubmit={(fileName, fileId) => {
          props.onChange({ ...props.value, message: `Selected ${fileName}`, fileId: fileId });
          props.onSubmit({ ...props.value, message: `Selected ${fileName}`, fileId: fileId });
        }}
      />
      }
      {props.inputRequirement.datasetSelect && <DatasetSelect
        label="Select dataset"
        datasetId={props.value.dataset}
        onChange={v => {
          props.onChange({ ...props.value, dataset: v });
          props.onSubmit({ ...props.value, dataset: v });
        }} />}
      {props.inputRequirement.jsonDataSchema &&
        <Stack spacing={2} sx={{
          p: 2,
          width: '100%',
          borderRadius: 2,
          backgroundColor: theme.palette.background.paper,
        }}>
          <StaticParamEditor
            schema={props.inputRequirement.jsonDataSchema.dataSchema}
            value={props.value.jsonData}
            onChange={jsonData => props.onChange({ ...props.value, jsonData })}
            resetTrigger={props.inputRequirement.jsonDataSchema}
          />
          <Button
            variant="contained"
            onClick={() => props.onSubmit(props.value)}
          >Submit</Button>
        </Stack>
      }
      {/* {props.inputRequirement.linkUpload && <LinkInput
          links={links}
          onChange={setLinks}
        />
        } */}
      {/* case "DefaultContentSettings": return <ContentSettingsInput
          defaults={inputConfig}
          onSubmit={contentSettings => props.onSubmit({ ...props.value, contentSettings })}
        />
        case "SelectPageToEdit": return <Card sx={{ p: 3 }}>
          {pageInEdit
            ? <><Typography variant="subtitle1">Editing:</Typography><Typography>{pageInEdit.label || pageInEdit.id}</Typography></>
            : <Typography variant="subtitle1" color='error'>Please select the content you want to edit by double clicking it.</Typography>
          }
        </Card> */}
      {/* {props.inputRequirement.control
          ? <ControlCommandSelect control={props.inputRequirement.control} onSubmit={cmd => {
            props.onChange({ ...props.value, command: cmd });
            props.onSubmit({ ...props.value, command: cmd });
          }} />
          : undefined
        } */}
      {/* {props.inputRequirement.tuning
          ? <TuningInputView
            inputRequirement={props.inputRequirement.tuning}
            value={props.value.tuning || { choice: TuningOption.Skip }}
            onChange={i => props.onChange({ ...props.value, tuning: i })}
            onSumbit={i => props.onSubmit({ ...props.value, tuning: i })}
          />
          : undefined
        } */}
    </Box>
  </Fade>
}

const CircularProgressWithIcon = (props: CircularProgressProps & { onClick: () => void }) => {
  return (
    <Box position="relative" display="inline-flex">
      <CircularProgress {...props} color="inherit" />
      <Box
        top={0}
        left={0}
        bottom={0}
        right={0}
        position="absolute"
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <IconButton onClick={props.onClick} color="inherit">
          <PauseRoundedIcon />
        </IconButton>
      </Box>
    </Box>
  );
};


function shouldHideChatBox(inputRequirement: TUserInputRequirement): boolean {
  return inputRequirement.choicesOnly
    || inputRequirement.datasetSelect
    || inputRequirement.fileSelect
    || inputRequirement.knowledgeSelect
    || Boolean(inputRequirement.jsonDataSchema)
    || Boolean(inputRequirement.webContent)
    || Boolean(inputRequirement.webInteractionOutcome)
    || Boolean(inputRequirement.codeExecution);
}


export default function ChatInput(props: {
  inputRequirment: TUserInputRequirement,
  value: IUserInput,
  onChange: (chatInput: IUserInput) => void,
  onSubmit: (chatInput: IUserInput) => void,
  onCancel: () => void,
  processing?: boolean,
  disabled?: boolean,
  conversation?: TMessage[],
  isAtBottom?: boolean,
  hidden?: boolean,
}): React.ReactElement {
  const [voiceDrawerOpen, setVoiceDrawerOpen] = React.useState(false);
  const [voiceInput, setVoiceInput] = React.useState('');
  const inputRef = React.useRef<HTMLDivElement>(null);
  const onSubmitRef = React.useRef(props.onSubmit);
  useEffect(() => {
    onSubmitRef.current = props.onSubmit;
  }, [props.onSubmit]);
  const webInteractionOutcome = useClientStore(state => state.webInteractionOutcome);
  const webInteractionOutcomeRef = React.useRef(webInteractionOutcome);
  useEffect(() => {
    webInteractionOutcomeRef.current = webInteractionOutcome;
  }, [webInteractionOutcome]);
  const lastChildAppClientId = useClientStore(state => state.lastChildAppClientId);
  const lastChildAppClientIdRef = React.useRef(lastChildAppClientId);
  useEffect(() => {
    lastChildAppClientIdRef.current = lastChildAppClientId;
  }, [lastChildAppClientId]);
  const autoSubmitQueue = React.useRef(Promise.resolve());
  const addError = useClientStore(state => state.addError);
  const setInterrupted = useClientStore(state => state.setInterrupted);

  const theme = useTheme();

  useEffect(() => {
    if (!props.processing && !props.disabled && !shouldHideChatBox(props.inputRequirment)) {
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [props, inputRef.current]);

  useEffect(() => {
    if (props.inputRequirment.webContent) {
      autoSubmitQueue.current = autoSubmitQueue.current.finally(async () => {
        const promise = new Promise(resolve => setTimeout(resolve, props.inputRequirment.webContent.delay * 1000));
        await promise;
        const webContent = await captureWebContent();
        onSubmitRef.current({ webContent });
      });
    }
    else if (props.inputRequirment.codeExecution) {
      autoSubmitQueue.current = autoSubmitQueue.current.finally(async () => {
        const codeExec = props.inputRequirment.codeExecution;
        let result;
        switch (codeExec.language) {
          case 'python':
          case 'python':
            result = await runPyCode(
              codeExec.code,
              codeExec.args,
              codeExec.functionName
            )
              .catch((error: unknown) => {
                const message = error instanceof Error
                  ? `${error.message}\n\nStack Trace:\n${error.stack}`
                  : `Unknown error during Python code execution: ${JSON.stringify(error)}`;
                addError({
                  name: 'Python Code Execution Error',
                  message,
                });
                setInterrupted('error');
                // return undefined to short-circuit
                return;
              });
            break;
          default:
            result = await runJsCode(
              codeExec.code,
              codeExec.args,
              codeExec.functionName
            )
              .catch((error: unknown) => {
                const message = error instanceof Error
                  ? `${error.message}\n\nStack Trace:\n${error.stack}`
                  : `Unknown error during JavaScript code execution: ${JSON.stringify(error)}`;
                addError({
                  name: 'JavaScript Code Execution Error',
                  message,
                });
                setInterrupted('error');
                return;
              });
            break;
        }
        const ajv = new Ajv({ strict: false });
        const validate = ajv.compile(codeExec.resultSchema);
        validate(result);
        if (validate.errors) {
          addError({
            name: 'Code Execution Error',
            message: `Code execution result does not match the expected schema.\nResult JSON: ${JSON.stringify(result, undefined, 2)}\nValidation Error: ${JSON.stringify(validate.errors, undefined, 2)}`,
          });
          setInterrupted('error');
          return;
        }
        onSubmitRef.current({ codeExecutionResult: result });
      });
    }
    else if (props.inputRequirment.webInteractionOutcome) {
      autoSubmitQueue.current = autoSubmitQueue.current.finally(
        () => onSubmitRef.current({ webInteractionOutcome: webInteractionOutcomeRef.current })
      );
    }
    else if (props.inputRequirment.runAppClientId) {
      autoSubmitQueue.current = autoSubmitQueue.current.finally(
        () => onSubmitRef.current({ runAppClientId: lastChildAppClientIdRef.current })
      );
    }
  }, [props.inputRequirment]);

  function isEmpty(userInput: IUserInput) {
    return !userInput.message?.trim()?.length;
  }

  function validateInput(userInput: IUserInput) {
    return true;
  }

  const isInputValid = validateInput(props.value);

  const errorMessage = isInputValid
    ? undefined : "Please enter a valid email address."

  const dictaphone = isDictphoneAvailable()
    ? <Dictaphone
      onChange={setVoiceInput}
      onStart={() => setVoiceDrawerOpen(true)}
      onEnd={() => {
        if (voiceInput.length > 0) {
          props.onChange({ ...props.value, message: `${props.value.message} ${voiceInput}` });
        }
        setVoiceDrawerOpen(false);
      }}
    />
    : <KeyboardArrowRightRoundedIcon fontSize="large" />;

  return <Stack spacing={2} sx={{
    width: '100%',
    maxHeight: '100%',
    display: props.hidden ? 'none' : 'flex',
    justifyContent: 'flex-end',
    // make the container clickthrough
    pointerEvents: 'none',
  }}>
    {!props.processing && !props.disabled
      ? <AdditionalInputView
        inputRequirement={props.inputRequirment}
        value={props.value}
        onChange={props.onChange}
        onSubmit={props.onSubmit}
        show={props.isAtBottom !== false}
      />
      : <></>
    }
    {shouldHideChatBox(props.inputRequirment)
      ? props.processing && <Button
        variant="contained"
        onClick={props.onCancel}
        sx={{
          borderRadius: '40px',
          pointerEvents: 'auto',
          textTransform: 'none',
          boxShadow: theme.shadows[5],
          transform: 'translateY(-4px)',
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        }}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <CircularProgressWithIcon size='1.5rem' onClick={props.onCancel} />
          <Typography variant="h6">Pause</Typography>
        </Stack>
      </Button>
      : <Box sx={{
        width: '100%',
        position: 'relative',
        borderRadius: 2,
        boxShadow: theme.shadows[8],
        background: `linear-gradient(to top right, ${lighten(theme.palette.primary.dark, 0.2)}, ${theme.palette.primary.dark})`,
        color: theme.palette.getContrastText(theme.palette.primary.dark),
        border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
        transform: 'translateY(-4px)',
        pointerEvents: 'auto',
      }}>
        <TextField
          disabled={props.disabled}
          value={props.value.message || ''}
          error={!isInputValid}
          helperText={errorMessage}
          onChange={e => props.onChange({ ...props.value, message: e.target.value })}
          onKeyDown={e => {
            if (!e.shiftKey && e.key === "Enter") {
              e.preventDefault();
              if (!props.processing) props.onSubmit(props.value);
            }
          }}
          inputRef={inputRef}
          fullWidth
          variant="outlined"
          multiline
          placeholder="Type a message..."
          minRows={1}
          maxRows={6}
          sx={{
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiInputBase-root': {
              padding: '16px 16px 50px 16px',
              borderRadius: '16px',
              color: 'inherit',
            },
            '& .MuiInputBase-input': {
              color: 'inherit',
              '&::placeholder': {
                color: 'inherit',
                opacity: 0.7,
              },
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                background: alpha(theme.palette.common.white, 0.3),
                borderRadius: '3px',
              },
              '&::-webkit-scrollbar-thumb:hover': {
                background: alpha(theme.palette.common.white, 0.5),
              },
              scrollbarWidth: 'thin',
              scrollbarColor: `${alpha(theme.palette.common.white, 0.3)} transparent`,
            },
            '& .MuiFormHelperText-root': {
              color: theme.palette.error.light,
            },
          }}
        />
        <Box sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          pb: 3,
          height: 8,
        }}>
          <Box>
            <IconButton
              size="medium"
              sx={{
                width: 6,
                height: 6,
                color: 'inherit',
              }}
            >
              {dictaphone}
            </IconButton>
          </Box>
          <Box>
            {props.processing
              ? <CircularProgressWithIcon size='2rem' onClick={props.onCancel} />
              : <IconButton
                onClick={() => props.onSubmit(props.value)}
                disabled={!isInputValid || isEmpty(props.value)}
                color="inherit"
                sx={{
                  width: 6,
                  height: 6,
                }}
              >
                <SendRoundedIcon fontSize="medium" />
              </IconButton>
            }
          </Box>
        </Box>
      </Box>}
    <Drawer open={voiceDrawerOpen} onClick={() => setVoiceDrawerOpen(false)} anchor="bottom" sx={{ zIndex: 10000 }}>
      <Stack spacing={3} sx={{
        p: { xs: 2, md: 3 },
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '30vh',
      }}>
        <Typography>{voiceInput}</Typography>
        <Pulse><KeyboardVoiceRoundedIcon sx={{ fontSize: '3rem' }} /></Pulse>
      </Stack>
    </Drawer>
  </Stack>
}
